import React, { useContext, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../context/User';
import { StatusContext } from '../context/Status';

import { Button, Container, Dropdown, Icon, Menu, Segment } from 'semantic-ui-react';
import { API, getLogo, getSystemName, isBD, isMobile, isRoot, isTenantAdmin, isTenantSite, showSuccess } from '../helpers';
import '../index.css';

const baseHeaderButtons = [
  {
    name: '首页',
    to: '/',
    icon: 'home'
  },
  {
    name: '渠道',
    to: '/channel',
    icon: 'sitemap',
    root: true
  },
  {
    name: '令牌',
    to: '/token',
    icon: 'key',
    auth: true
  },
  {
    name: '兑换',
    to: '/redemption',
    icon: 'dollar sign',
    root: true
  },
  {
    name: '充值',
    to: '/topup',
    icon: 'cart',
    auth: true
  },
  {
    name: '用户',
    to: '/user',
    icon: 'user',
    root: true,
    bd: true
  },
  {
    name: '用户',
    to: '/tenant-admin/users',
    icon: 'user',
    tenantAdmin: true
  },
  {
    name: '转账充值',
    to: '/manual-topup',
    icon: 'exchange',
    root: true
  },
  {
    name: '第三方应用',
    to: '/third-party-apps',
    icon: 'plug',
    root: true
  },
  {
    name: 'Seedance 资产',
    to: '/seedance-assets',
    icon: 'images',
    root: true
  },
  {
    name: '消费发票',
    to: '/consumption-invoice',
    icon: 'file alternate outline',
    root: true
  },
  {
    name: '消费汇总',
    to: '/consumption-summary',
    icon: 'chart bar',
    root: true,
    bd: true
  },
  {
    name: '消费汇总',
    to: '/tenant-admin/consumption',
    icon: 'chart bar',
    tenantAdmin: true
  },
  {
    name: '充值记录',
    to: '/tenant-admin/topups',
    icon: 'exchange',
    tenantAdmin: true
  },
  {
    name: '财务月报',
    to: '/financial-summary',
    icon: 'calculator',
    root: true
  },
  {
    name: '日志',
    to: '/log',
    icon: 'book',
    auth: true
  },
  {
    name: '设置',
    to: '/setting',
    icon: 'setting',
    auth: true
  },
  {
    name: '测试',
    to: '/test',
    icon: 'flask',
    auth: true
  },
  {
    name: '文档',
    to: '/doc',
    icon: 'book',
    auth: true
  },
  {
    name: '关于',
    to: '/about',
    icon: 'info circle',
    hideForTenant: true
  }
];

const getHeaderButtons = (chatEnabled, user) => {
  const buttons = [...baseHeaderButtons];
  if (user && !isRoot()) {
    buttons[0] = {
      ...buttons[0],
      name: '模型'
    };
  }
  if (chatEnabled) {
    buttons.splice(1, 0, {
      name: '聊天',
      to: '/chat',
      icon: 'comments'
    });
  }
  return buttons;
};

const isActivePath = (pathname, target) => {
  if (target === '/') {
    return pathname === '/';
  }
  return pathname === target || pathname.startsWith(`${target}/`);
};

const Brand = ({ logo, systemName, mobile = false }) => (
  <Menu.Item as={Link} to='/' className={`modern-brand ${mobile ? 'modern-brand-mobile' : ''}`}>
    <span className='modern-brand-mark'>
      <img src={logo} alt='logo' />
    </span>
    <span className='modern-brand-copy'>
      <span className='modern-brand-title'>{systemName}</span>
      {!mobile && <span className='modern-brand-subtitle'>模型超市</span>}
    </span>
  </Menu.Item>
);

const Header = () => {
  const [userState, userDispatch] = useContext(UserContext);
  const [statusState] = useContext(StatusContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [showSidebar, setShowSidebar] = useState(false);
  const systemName = statusState?.status?.system_name || getSystemName();
  const logo = statusState?.status?.logo || getLogo();
  const visibleButtons = getHeaderButtons(
    statusState?.status?.chat_link || localStorage.getItem('chat_link'),
    userState.user
  ).filter((button) => {
    if (button.root && !isRoot() && !(button.bd && isBD())) return false;
    if (button.tenantAdmin && !isTenantAdmin()) return false;
    if (button.auth && !userState.user) return false;
    // 租户站点隐藏部分入口
    if (button.hideForTenant && isTenantSite(statusState?.status)) return false;
    return true;
  });

  async function logout() {
    setShowSidebar(false);
    await API.get('/api/user/logout');
    showSuccess('退出成功!');
    userDispatch({ type: 'logout' });
    localStorage.removeItem('user');
    navigate('/login');
  }

  const toggleSidebar = () => {
    setShowSidebar(!showSidebar);
  };

  const renderButtons = (mobile) => {
    return visibleButtons.map((button) => {
      const active = isActivePath(location.pathname, button.to);
      if (mobile) {
        return (
          <Menu.Item
            key={button.name}
            className={`modern-mobile-nav-item ${active ? 'active' : ''}`}
            onClick={() => {
              navigate(button.to);
              setShowSidebar(false);
            }}
          >
            <Icon name={button.icon} />
            <span>{button.name}</span>
          </Menu.Item>
        );
      }
      return (
        <Menu.Item
          key={button.name}
          as={Link}
          to={button.to}
          active={active}
          className='modern-nav-item'
        >
          <Icon name={button.icon} />
          {button.name}
        </Menu.Item>
      );
    });
  };

  if (isMobile()) {
    return (
      <>
        <Menu borderless size='large' className='modern-header modern-header-mobile'>
          <Container className='modern-header-shell'>
            <Brand logo={logo} systemName={systemName} mobile />
            <Menu.Menu position='right' className='modern-header-actions'>
              <Menu.Item
                className={`modern-mobile-toggle ${showSidebar ? 'active' : ''}`}
                onClick={toggleSidebar}
              >
                <Icon name={showSidebar ? 'close' : 'sidebar'} />
              </Menu.Item>
            </Menu.Menu>
          </Container>
        </Menu>
        {showSidebar ? (
          <Container className='main-content'>
            <Segment className='modern-header-drawer'>
              <Menu secondary vertical className='modern-mobile-nav'>
                {renderButtons(true)}
              </Menu>
              <div className='modern-mobile-auth'>
                {userState.user ? (
                  <Button negative fluid onClick={logout}>
                    退出
                  </Button>
                ) : (
                  <>
                    <Button
                      primary
                      fluid
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/login');
                      }}
                    >
                      登录
                    </Button>
                    <Button
                      basic
                      fluid
                      onClick={() => {
                        setShowSidebar(false);
                        navigate('/register');
                      }}
                    >
                      注册
                    </Button>
                  </>
                )}
              </div>
            </Segment>
          </Container>
        ) : (
          <></>
        )}
      </>
    );
  }

  return (
    <Menu borderless className='modern-header'>
      <Container className='modern-header-shell'>
        <Brand logo={logo} systemName={systemName} />
        <Menu.Menu className='modern-header-nav'>{renderButtons(false)}</Menu.Menu>
        <Menu.Menu position='right' className='modern-header-actions'>
          {userState.user ? (
            <Dropdown
              pointing='top right'
              icon={null}
              className='modern-user-dropdown item'
              trigger={
                <span className='modern-user-trigger'>
                  <Icon name='user circle outline' />
                  <span>{userState.user.username}</span>
                  <Icon name='chevron down' size='small' />
                </span>
              }
            >
              <Dropdown.Menu>
                <Dropdown.Item onClick={logout}>
                  <Icon name='sign out' />
                  退出
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <Menu.Item as={Link} to='/login' className='modern-login-entry'>
              <Icon name='sign in' />
              登录
            </Menu.Item>
          )}
        </Menu.Menu>
      </Container>
    </Menu>
  );
};

export default Header;
